/**
 * The consolidated executive dashboard (KAN-42 / KAN-60 / KAN-97).
 *
 * The audience is firm leadership, looking at this briefly and infrequently, so
 * the layout is built around one question: what can somebody take in during
 * thirty seconds before a meeting? Everything below follows from that.
 *
 * **Nothing on this page is computed.** Every figure, comparison, direction and
 * favourability judgement arrives from the API, and the two judgement calls that
 * could plausibly be made here — which way is good for a metric, and what the
 * change is — come from shared code the API uses too. That is the whole point of
 * KAN-96's aggregation layer: a dashboard that recalculated even a percentage
 * would be a second implementation of it, and the first thing to disagree.
 *
 * **An absent figure is rendered as absent.** Five of the nine metrics have no
 * module yet, and a sixth can go dark when its sync fails. Each of those states
 * says what it is and names the ticket or the reason. A zero would be worse than
 * useless here — a zero cash position from a failed bank feed is the single most
 * alarming thing this page could show, and it would not be true.
 *
 * **Colour is never the only signal.** Every tile that makes a judgement prints
 * the word beside the arrow. Leadership screenshots these into board packs,
 * which is a greyscale, screen-reader-less context where colour carries nothing.
 */
import { Suspense, lazy, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ABOVE_THE_FOLD,
  DIRECTION_LABELS,
  EXECUTIVE_METRIC_META,
  EXECUTIVE_RANGES,
  EXECUTIVE_RANGE_META,
  FAVOURABILITY_LABELS,
  directionOf,
  favourabilityOf,
  formatBps,
  type ConsistencySummary,
  type ExecutiveHistoryPoint,
  type ExecutiveMetric,
  type ExecutiveRange,
  type MetricFavourability,
  type MetricUnavailableReason,
} from '@accounting/shared'
import { ApiError, apiFetch } from '../lib/api'
import { formatDateTime, formatMoney, formatRelative } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge } from '../ui/Badge'
import { Field, Select } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { cx } from '../ui/cx'
import {
  ChevronIcon,
  TrendDownIcon,
  TrendFlatIcon,
  TrendUpIcon,
  UnavailableIcon,
} from '../ui/icons'

/**
 * The charts, split out of this bundle.
 *
 * Recharts is the largest single dependency this SPA has and it draws nothing
 * above the fold, so it is fetched after the tiles are on screen rather than
 * before them. That is the difference between the headline figures appearing
 * immediately and their waiting on a charting library to parse — and KAN-97's
 * latency budget is about the figures, not the charts.
 */
const ExecutiveTrends = lazy(() => import('./ExecutiveTrends'))

/** Exported for the lazily loaded charts, which take the whole snapshot. */
export interface ExecutiveSnapshot {
  capturedAt: string
  range: ExecutiveRange
  metrics: ExecutiveMetric[]
  history: ExecutiveHistoryPoint[]
  consistency: ConsistencySummary | null
}

export function ExecutiveDashboard() {
  const [range, setRange] = useState<ExecutiveRange>('month')

  const query = useQuery({
    queryKey: ['executive', range],
    queryFn: () => apiFetch<{ snapshot: ExecutiveSnapshot | null }>(`/executive?range=${range}`),
  })

  const snapshot = query.data?.snapshot ?? null

  /**
   * The tiles, split at the fold. The order is the API's — importance, decided
   * in shared code — so this page never reorders what leadership sees.
   */
  const headline = snapshot?.metrics.slice(0, ABOVE_THE_FOLD) ?? []
  const secondary = snapshot?.metrics.slice(ABOVE_THE_FOLD) ?? []

  return (
    <>
      <PageHeader
        title="Executive dashboard"
        description="Every figure the firm is measured on, in one place, each one drilling into the module it came from."
        actions={
          <Field id="range" label="Compared with" hint={null}>
            <Select
              id="range"
              value={range}
              onChange={(event) => setRange(event.target.value as ExecutiveRange)}
            >
              {EXECUTIVE_RANGES.map((option) => (
                <option key={option} value={option}>
                  {EXECUTIVE_RANGE_META[option].label}
                </option>
              ))}
            </Select>
          </Field>
        }
      />

      {query.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(query.error)}
        </Alert>
      )}

      {query.isPending && <p className="text-sm text-ink-600">Loading the dashboard…</p>}

      {/*
        * No snapshot at all, which is the current state of this deployment: the
        * Clio and QuickBooks connections are waiting on credentials, so nothing
        * has ever synced and nothing has been captured. Said plainly rather than
        * rendered as nine empty tiles, which would read as a firm with no money.
        */}
      {!query.isPending && !snapshot && !query.error && (
        <Alert tone="info" title="No snapshot has been taken yet">
          The dashboard is built from a stored snapshot, retaken automatically after every sync that
          changes a figure. Nothing has synced on this deployment yet. Somebody with shell access can
          take one now with <code className="font-mono text-xs">node ace executive:refresh</code>.
        </Alert>
      )}

      {snapshot && (
        <>
          <Freshness snapshot={snapshot} />

          {/*
            * Above the fold. Two columns on a phone rather than one: leadership
            * opening this on a phone is a stated requirement, and a single
            * column would put the fifth tile three screens down.
            */}
          <section aria-labelledby="headline" className="mb-8">
            <h2 id="headline" className="sr-only">
              Headline figures
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
              {headline.map((metric) => (
                <MetricTile key={metric.key} metric={metric} range={snapshot.range} prominent />
              ))}
            </div>
          </section>

          <section aria-labelledby="secondary" className="mb-8">
            <h2 id="secondary" className="mb-3 font-display text-lg font-semibold text-brand-900">
              Planning figures
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {secondary.map((metric) => (
                <MetricTile key={metric.key} metric={metric} range={snapshot.range} />
              ))}
            </div>
          </section>

          {/*
            * A plain sentence as the fallback rather than a skeleton the size of
            * the charts. A grey block that tall reads as broken layout, and the
            * charts are supplementary — nobody is blocked while they arrive.
            */}
          <Suspense fallback={<p className="text-sm text-ink-600">Loading trends…</p>}>
            <ExecutiveTrends snapshot={snapshot} />
          </Suspense>
        </>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ *
 * Freshness and verification
 * ------------------------------------------------------------------------ */

/**
 * When the figures were taken, and whether they were verified against source.
 *
 * The consistency result is shown rather than kept in a log, because it is the
 * answer to the question this dashboard exists to pre-empt: "does this match
 * what Finance sees?" A green line saying the figures were checked against every
 * module is worth more here than on any other screen.
 */
function Freshness({ snapshot }: { snapshot: ExecutiveSnapshot }) {
  const { consistency } = snapshot
  const diverged = (consistency?.divergedCount ?? 0) > 0

  return (
    <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-ink-200 pb-4 text-xs text-ink-600">
      <span>
        Captured {formatRelative(snapshot.capturedAt)}{' '}
        <span className="text-ink-500">({formatDateTime(snapshot.capturedAt)})</span>
      </span>

      {consistency && !diverged && (
        <Badge tone="positive">
          {consistency.agreesCount} of {consistency.comparedCount} verified against source
        </Badge>
      )}

      {/*
        * Divergence is the failure KAN-96 exists to prevent, so it is stated on
        * the dashboard rather than only in a log. If the figures here disagree
        * with the modules they came from, the person reading them needs to know
        * before they quote one.
        */}
      {diverged && (
        <Badge tone="critical">
          {consistency!.divergedCount} figure{consistency!.divergedCount === 1 ? '' : 's'} disagree
          with source — do not quote
        </Badge>
      )}

      {consistency && consistency.staleCount > 0 && (
        <Badge tone="warning">
          {consistency.staleCount} moved since capture
        </Badge>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------ *
 * A tile
 * ------------------------------------------------------------------------ */

/**
 * Tone per favourability.
 *
 * Restrained on purpose: a dashboard where four of nine tiles are red reads as a
 * crisis regardless of the numbers, and leadership then stops opening it. The
 * background stays white and only the comparison line carries colour, so the
 * figures themselves are never tinted into a verdict.
 */
const FAVOURABILITY_TONES: Record<MetricFavourability, string> = {
  favourable: 'text-emerald-700',
  unfavourable: 'text-red-700',
  neutral: 'text-ink-600',
}

const DIRECTION_ICONS = {
  up: TrendUpIcon,
  down: TrendDownIcon,
  flat: TrendFlatIcon,
}

function MetricTile({
  metric,
  range,
  prominent = false,
}: {
  metric: ExecutiveMetric
  range: ExecutiveRange
  prominent?: boolean
}) {
  const meta = EXECUTIVE_METRIC_META[metric.key]

  const body = (
    <>
      <p className="text-[11px] font-semibold tracking-[0.12em] text-ink-500 uppercase">
        {meta.label}
      </p>

      {metric.status === 'ok' ? (
        <>
          <p
            className={cx(
              'mt-1 font-display font-semibold tabular-nums text-brand-900',
              prominent ? 'text-2xl' : 'text-xl'
            )}
          >
            {formatValue(metric)}
          </p>
          <Comparison metric={metric} range={range} />
        </>
      ) : (
        <Absent metric={metric} />
      )}

      {/*
        * The source's own freshness, per tile rather than once per page. These
        * refresh on different cadences — time entries hourly, trust overnight —
        * so a single page-level timestamp would describe the freshest source and
        * misdescribe the rest.
        */}
      {metric.status === 'ok' && (
        <p className="mt-2 text-[11px] leading-snug text-ink-500">
          As of {formatRelative(metric.asOf)}
          {metric.freshness.lastSuccessAt === null && (
            <span className="text-accent-700"> · source has never synced</span>
          )}
        </p>
      )}
    </>
  )

  const shell = cx(
    'rounded-lg border bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(21,26,108,0.04)]',
    metric.status === 'ok' ? 'border-ink-200' : 'border-dashed border-ink-300'
  )

  /**
   * Every available tile drills into its module — the requirement that makes
   * this a starting point rather than a dead end. A tile whose module has no
   * screen yet is deliberately *not* a link: a link that goes nowhere is worse
   * than none, and the reason it cannot be followed is already printed on it.
   */
  if (metric.status === 'ok' && meta.drillTo) {
    return (
      <Link
        to={meta.drillTo}
        title={`${meta.description} Opens the ${meta.label.toLowerCase()} module.`}
        className={cx(
          shell,
          'group block transition-colors hover:border-brand-300 hover:bg-brand-50/40'
        )}
      >
        {body}
        <span className="mt-2 flex items-center gap-1 text-[11px] font-medium text-brand-700">
          Open module
          <ChevronIcon
            width="12"
            height="12"
            className="transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </Link>
    )
  }

  return (
    <div className={shell} title={meta.description}>
      {body}
    </div>
  )
}

/**
 * The comparison line: how much, which way, and whether that is good news.
 *
 * All three, always — KAN-97's objection to "$847k AR" is that a bare figure is
 * not a decision input. The percentage is omitted where the baseline was zero,
 * because a rise from nothing has no percentage and inventing one would be the
 * only false number on the page.
 */
function Comparison({ metric, range }: { metric: ExecutiveMetric; range: ExecutiveRange }) {
  if (!metric.comparison) {
    /**
     * Said rather than left blank. A missing comparison is nearly always "we
     * have not been snapshotting long enough", and a reader who is not told that
     * assumes the figure has not moved.
     */
    return (
      <p className="mt-1 text-xs text-ink-500">
        No snapshot from {EXECUTIVE_RANGE_META[range].label} ago to compare with
      </p>
    )
  }

  const { changeMinor, changeBps } = metric.comparison
  const direction = directionOf(changeMinor)
  const favourability = favourabilityOf(metric.key, changeMinor)
  const Arrow = DIRECTION_ICONS[direction]

  return (
    <p
      className={cx(
        'mt-1 flex flex-wrap items-center gap-x-1.5 text-xs font-medium',
        FAVOURABILITY_TONES[favourability]
      )}
    >
      <Arrow className="shrink-0" />
      {/*
        * The word as well as the arrow and the colour. This is the accessibility
        * requirement and the greyscale-print requirement in one — see the file
        * header.
        */}
      <span>
        {DIRECTION_LABELS[direction]}
        {changeBps !== null && ` ${formatBps(Math.abs(changeBps))}`}
      </span>
      <span className="font-normal text-ink-500">{EXECUTIVE_RANGE_META[range].comparisonLabel}</span>
      {favourability !== 'neutral' && (
        <span className="sr-only">({FAVOURABILITY_LABELS[favourability]})</span>
      )}
    </p>
  )
}

/**
 * What each kind of absence says.
 *
 * Four genuinely different situations, and collapsing them into "unavailable"
 * would waste the one chance this page has to tell a reader whether somebody is
 * already fixing it. `module_not_built` is a roadmap answer and names its
 * ticket; `sync_failed` is somebody's job this morning.
 */
const ABSENCE_COPY: Record<MetricUnavailableReason, string> = {
  module_not_built: 'Not built yet',
  sync_failed: 'Source sync failed',
  no_source_data: 'Not yet calculated',
  source_error: 'Calculation failed',
}

function Absent({ metric }: { metric: ExecutiveMetric }) {
  const meta = EXECUTIVE_METRIC_META[metric.key]

  if (metric.status === 'restricted') {
    return (
      <>
        <p className="mt-1 font-display text-base font-semibold text-ink-500">Not shown</p>
        <p className="mt-1 text-xs leading-snug text-ink-600">
          Your role does not hold the grant this figure requires.
        </p>
      </>
    )
  }

  const reason = metric.reason ?? 'module_not_built'

  return (
    <>
      {/*
        * Never a zero and never a blank. The em dash is a deliberate
        * placeholder: it occupies the space a figure would and cannot be
        * misread as one.
        */}
      <p className="mt-1 flex items-center gap-1.5 font-display text-xl font-semibold text-ink-400">
        <UnavailableIcon width="16" height="16" />
        <span aria-hidden="true">—</span>
        <span className="sr-only">no figure available</span>
      </p>

      <p
        className={cx(
          'mt-1 text-xs font-medium',
          reason === 'sync_failed' || reason === 'source_error'
            ? 'text-accent-700'
            : 'text-ink-600'
        )}
      >
        {ABSENCE_COPY[reason]}
        {reason === 'module_not_built' && (
          /**
           * The ticket must not wrap. In a narrow tile it otherwise breaks after
           * the hyphen and renders as "KAN-" over "76", which is not a reference
           * anybody can look up.
           */
          <span className="font-normal whitespace-nowrap text-ink-500">
            {' · '}
            {meta.sourceTicket}
          </span>
        )}
      </p>

      {reason === 'sync_failed' && (
        <p className="mt-1 text-[11px] leading-snug text-ink-600">
          The last sync of {metric.freshness.resources.join(', ')} failed, so no figure is shown
          rather than an older one presented as current.
        </p>
      )}

      {reason === 'source_error' && metric.reasonDetail && (
        <p className="mt-1 truncate text-[11px] text-ink-500" title={metric.reasonDetail}>
          {metric.reasonDetail}
        </p>
      )}
    </>
  )
}

/**
 * Formats a figure in its own unit.
 *
 * Money goes through the shared formatter — the one in `@accounting/shared`,
 * never a second one — and a rate through the shared basis-point formatter. Both
 * are presentation only; nothing here feeds back into arithmetic, because there
 * is no arithmetic on this page.
 */
function formatValue(metric: ExecutiveMetric): string {
  if (metric.valueMinor === null) return '—'

  const { unit } = EXECUTIVE_METRIC_META[metric.key]
  if (unit === 'bps') return formatBps(metric.valueMinor)
  if (unit === 'count') return String(metric.valueMinor)
  return formatMoney(metric.valueMinor)
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  return 'Something went wrong loading the dashboard.'
}
